import { motion, AnimatePresence } from 'framer-motion'
import { NavLink } from 'react-router-dom'
import {
  X,
  BarChart3,
  Sparkles,
  ListChecks,
  Skull,
  Settings,
  HelpCircle,
  Gem,
  Flame,
  Trophy,
  Users,
} from 'lucide-react'
import { useUserStore } from '@/stores/useUserStore'
import { useCharacterStore } from '@/stores/useCharacterStore'
import type { CharacterId } from '@/types'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

const CHARACTER_EMOJIS: Record<CharacterId, string> = {
  hana: '💕',
  yuki: '❄️',
  momo: '🍑',
  sumire: '💜',
}

const menuItems = [
  { path: '/partners', label: 'Partners', Icon: Users },
  { path: '/stats', label: 'Statistics', Icon: BarChart3 },
  { path: '/achievements', label: 'Achievements', Icon: Trophy },
  { path: '/routines', label: 'Routines', Icon: ListChecks },
  { path: '/projects', label: 'Boss Battles', Icon: Skull },
  { path: '/gacha', label: 'Gacha', Icon: Sparkles },
  { path: '/settings', label: 'Settings', Icon: Settings },
]

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { level, gems, streakDays, activeCharacterId } = useUserStore()
  const { getAffectionLevel } = useCharacterStore()
  const affectionLevel = getAffectionLevel(activeCharacterId)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Sidebar panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-72 bg-bg-surface z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-primary/20 to-secondary/20 border-b border-white/5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-xl text-gradient">
                  WAIFUDO
                </h2>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Profile card */}
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-bg-card to-bg-dark flex items-center justify-center text-3xl">
                  {CHARACTER_EMOJIS[activeCharacterId]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-gradient-to-r from-secondary to-secondary-dark rounded-full text-xs font-bold">
                      Lv. {level}
                    </span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((heart) => (
                        <span
                          key={heart}
                          className={`text-xs ${
                            heart <= affectionLevel ? 'text-red-400' : 'text-bg-dark'
                          }`}
                        >
                          ♥
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary mt-1">
                    Adventurer
                  </p>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-2 p-4 border-b border-white/5">
              <div className="flex items-center gap-2 bg-bg-card rounded-xl px-3 py-2">
                <Gem className="text-gem" size={18} />
                <span className="font-bold">{gems}</span>
              </div>
              <div className="flex items-center gap-2 bg-bg-card rounded-xl px-3 py-2">
                <Flame className="text-accent" size={18} />
                <span className="font-bold">{streakDays} days</span>
              </div>
            </div>

            {/* Navigation links */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {menuItems.map(({ path, label, Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-primary/20 text-primary'
                        : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                    }`
                  }
                >
                  <Icon size={20} />
                  <span className="font-semibold">{label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-white/5">
              <button className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-text-muted hover:bg-white/5 transition-colors">
                <HelpCircle size={20} />
                <span className="font-semibold">Help & About</span>
              </button>
              <p className="text-xs text-text-muted text-center mt-3">
                WaifuDo v0.1.0
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
